/** One category's decision, as it is shown and as it is taken - Scope 3's Categories tab (S18, S25).
 *
 * Moved out of `Scope3.tsx` unchanged when the page became a table page (ADR-0212): the row shows
 * who made the judgement and when (`DecidedBy`), and the form that records one (`DecisionForm`) now
 * opens in a side drawer instead of under its row. Every refusal, the stale-decision conflict panel
 * and the post-close retry are exactly what they were; the rules are written on the two functions.
 */

import { useState } from "react";

import { useReader } from "../api/profile";
import {
  DECISIONS,
  isDecisionConflict,
  needsReason,
  provenanceOf,
  relevanceLabel,
  stamp,
  useDecideScope3Relevance,
  versionSeen,
} from "../api/scope3";
import type { Provenance } from "../api/scope3";
import type { DecidableRelevance, Scope3Category } from "../api/scope3";
import { isPeriodClosed } from "../api/refusal";
import { FieldError } from "../components/FieldError";
import { WriteFailed } from "../components/Loaded";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { useFormChecks } from "../components/useFormChecks";
import { PostCloseRetry } from "./PeriodLock";
import styles from "./Screen.module.css";
import own from "./Scope3.module.css";

/** Who made the judgement, and when - or the plain statement that the row does not say (S25).
 *
 * **Every branch is a different fact and each has its own `data-provenance`**, so a test can tell
 * *nobody has acted* (renders nothing), *a colleague decided on this date*, *a decision taken back*,
 * *a person since removed* and *a decision with no author on record* apart without reading prose.
 * The last is stated, not blanked: a blank beside *Excluded* reads as an omission, a sentence as a
 * decision. A name is never shown - only *you* or *a colleague* - and the identifier never.
 */
export function DecidedBy({
  category,
  provenance,
  testId,
}: {
  category: Scope3Category;
  provenance: Provenance;
  /** Where the same sentence is shown a second time on one page (the conflict panel), so a test
   * can tell the row's from the panel's. The default is the row's. */
  testId?: string;
}) {
  const reader = useReader();
  if (provenance.kind === "none") return null;
  const verb = provenance.kind !== "unattributed" && provenance.withdrawn ? "Withdrawn" : "Decided";
  return (
    <span
      className={own.provenance}
      data-testid={testId ?? `scope3-decided-by-${category.number}`}
      data-provenance={provenance.kind}
      data-reader-decided={provenance.kind === "named" ? provenance.who === "you" : undefined}
    >
      {provenance.kind === "unattributed" ? (
        "No decider or date is on record for this decision."
      ) : (
        <>
          {verb}
          {provenance.at ? (
            <>
              {" "}
              <time dateTime={provenance.at}>{stamp(provenance.at, reader)}</time>
            </>
          ) : null}{" "}
          by {provenance.kind === "removed" ? "someone since removed" : provenance.who}
        </>
      )}
    </span>
  );
}

/** One category's decision, as a form under its row (S18).
 *
 * **Three answers and a retraction, and none of them is a quantity.** The reason is asked for only
 * where the server requires one and is not sent otherwise, because the server refuses a reason on
 * an answer that is not an exclusion - a form that collected one anyway would meet that refusal on
 * every Relevant. The regulatory sentence a refusal carries comes from the server and is shown as
 * written (`WriteFailed`); none is typed here.
 *
 * **Never defaulted.** A category nobody has decided opens with nothing selected and the submit
 * withheld, so "Not assessed" cannot be turned into a decision by pressing the button that was
 * already under the cursor.
 */
export function DecisionForm({
  category,
  yearEnd,
  meId,
  refreshing,
  onDone,
  onCancel,
  onReload,
}: {
  category: Scope3Category;
  yearEnd: string;
  /** The signed-in person, so the conflict panel can say *you* or *a colleague* (rule 4). */
  meId: string | undefined;
  /** Whether the inventory is being re-read, so the panel can say it is looking. */
  refreshing: boolean;
  onDone: (recorded: { message: string; already: boolean }) => void;
  onCancel: () => void;
  onReload: () => void;
}) {
  const decide = useDecideScope3Relevance();
  const current = category.relevance;
  const [relevance, setRelevance] = useState<DecidableRelevance | "">(
    current === "relevant" || current === "not_applicable" || current === "excluded"
      ? current
      : "",
  );
  const [reason, setReason] = useState(category.justification ?? "");
  const [refusal, setRefusal] = useState<unknown>(null);
  // **What this reader saw, captured once when the form opened** - with the answer and the reason
  // above, which are read from the same `category` at the same moment. It is state and not a
  // read of `category.version` at submit: the inventory can be re-fetched under an open form, and
  // sending the newer number would turn a stale decision into one that looks fresh.
  const [seenVersion, setSeenVersion] = useState<number | null>(() => versionSeen(category));

  const reasonOwed = needsReason(relevance);
  const [checks, attach] = useFormChecks();
  // A refusal the server makes (a stale decision, a closed year) moves focus to its sentence.
  useFocusRefusal(checks.element, refusal);
  const conflict = isDecisionConflict(refusal) ? refusal : null;
  // The row has moved from what this person saw *and the page has now read the move*. Until then
  // the panel does not claim to know what is on record.
  const moved = conflict !== null && versionSeen(category) !== seenVersion;

  /** Record the decision, optionally with an Admin's reason for a year that has closed (ADR-0094).
   * The form keeps its answer, so the retry after `period_closed` is one field. */
  function submit(postCloseReason?: string) {
    // **The button is never greyed for these** (Q-06, G-Wadmin-14): pressing it with the reason empty
    // says so beside the reason and puts the cursor there. "A reason is required" used to sit in the
    // radio's help text while the empty box itself said nothing.
    const ok = checks.run([
      { field: "relevance", ok: relevance !== "", message: "Choose what the company has decided." },
      {
        field: "reason",
        ok: !reasonOwed || reason.trim() !== "",
        message: "Say why. An exclusion or a not-applicable answer needs a reason a reader can check.",
      },
    ]);
    if (!ok || relevance === "") return;
    setRefusal(null);
    void decide
      .mutateAsync({
        category: category.number,
        financialYearEnd: yearEnd,
        relevance,
        justification: reasonOwed ? reason.trim() : null,
        expectedVersion: seenVersion,
        postCloseReason,
      })
      .then(
        (result) =>
          onDone({
            already: result.already_recorded,
            message: result.already_recorded
              ? `${category.name}: that was already the decision on record, so nothing changed.`
              : `${category.name}: recorded as ${relevanceLabel(result.category.relevance)}.`,
          }),
        (error: unknown) => {
          setRefusal(error);
          // The remedy begins with looking, so the page does the looking itself: the panel
          // below needs the row as it now is, and asking the person to click for it first
          // would be one more step between them and the thing they came to read.
          if (isDecisionConflict(error)) onReload();
        },
      );
  }

  return (
    <form
      ref={attach}
      className={own.form}
      noValidate
      data-testid={`scope3-decide-form-${category.number}`}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <fieldset className={own.fieldset}>
        <legend className={own.legend}>
          What has the company decided about category {category.number}, {category.name}?
        </legend>
        {DECISIONS.map((option) => (
          <label key={option.value} className={own.option}>
            <input
              type="radio"
              name={`scope3-decision-${category.number}`}
              value={option.value}
              checked={relevance === option.value}
              data-field={option === DECISIONS[0] ? "relevance" : undefined}
              aria-invalid={option === DECISIONS[0] && checks.errors.relevance !== undefined ? true : undefined}
              data-testid={`scope3-decide-option-${option.value}`}
              onChange={() => {
                checks.touch("relevance");
                setRelevance(option.value);
              }}
            />
            <span>
              <strong>{option.label}</strong>
              <span className={own.means}>{option.means}</span>
            </span>
          </label>
        ))}
        {current !== "not_assessed" ? (
          <label className={own.option}>
            <input
              type="radio"
              name={`scope3-decision-${category.number}`}
              value="not_assessed"
              checked={relevance === "not_assessed"}
              data-testid="scope3-decide-option-not_assessed"
              onChange={() => setRelevance("not_assessed")}
            />
            <span>
              <strong>Withdraw the decision</strong>
              <span className={own.means}>
                Back to Not assessed - nobody has looked. Use this to correct a decision made in
                error, not to leave a category out.
              </span>
            </span>
          </label>
        ) : null}
        <FieldError {...checks.say("relevance")} />
      </fieldset>
      {reasonOwed ? (
        <label className={own.reason}>
          <span className={own.legend}>Why?</span>
          <textarea
            className={own.textarea}
            {...checks.field("reason")}
            data-testid="scope3-decide-reason"
            value={reason}
            maxLength={4000}
            rows={3}
            onChange={(event) => {
              checks.touch("reason");
              setReason(event.target.value);
            }}
          />
          <FieldError {...checks.say("reason")} />
        </label>
      ) : null}
      <div className={own.buttons}>
        <button
          type="submit"
          className={styles.primary}
          data-testid="scope3-decide-submit"
          disabled={decide.isPending || conflict !== null}
        >
          {decide.isPending ? "Recording…" : "Record decision"}
        </button>
        <button
          type="button"
          className={own.ghost}
          data-testid="scope3-decide-cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
      {conflict ? (
        /* **A third kind of failure, and neither `WriteFailed` branch says it.** Not a bad
           request (the form was right) and not an unknown outcome (we know it did not land): a
           colleague's decision for this category is on record and is not the one this person saw.
           The remedy is to look, and decide again if they still disagree - so the panel shows what
           is on record and who put it there (as the row does: *you* or *a colleague*, never a
           name), and "Decide again" is what re-arms the submit, against the version just shown.
           Nothing was overwritten, which is stated because it is the first thing somebody in this
           position wonders. */
        <div
          className={own.conflict}
          role="alert"
          data-testid="scope3-decide-conflict"
          data-moved={moved}
        >
          <p>
            <strong>Your decision was not recorded.</strong> {conflict.message}
          </p>
          {moved ? (
            <>
              <div
                className={own.onRecord}
                data-testid="scope3-decide-conflict-on-record"
                data-relevance={category.relevance}
              >
                <span>
                  <strong>On record now:</strong> {relevanceLabel(category.relevance)}
                </span>
                {category.justification ? <span>{category.justification}</span> : null}
                <DecidedBy
                  category={category}
                  provenance={provenanceOf(category, meId)}
                  testId={`scope3-conflict-decided-by-${category.number}`}
                />
              </div>
              <div className={own.buttons}>
                <button
                  type="button"
                  className={own.ghost}
                  data-testid="scope3-decide-conflict-again"
                  onClick={() => {
                    // Adopt the version the person has just been shown - not the one they
                    // opened the form on - and keep their draft, so deciding again is a click and
                    // not a retype. The choice is still theirs: they may change it first.
                    setSeenVersion(versionSeen(category));
                    setRefusal(null);
                  }}
                >
                  Decide again
                </button>
                <button
                  type="button"
                  className={own.ghost}
                  data-testid="scope3-decide-conflict-keep"
                  onClick={onCancel}
                >
                  Keep what is on record
                </button>
              </div>
            </>
          ) : (
            <>
              {refreshing ? (
                <p data-testid="scope3-decide-conflict-looking">Looking up what is on record…</p>
              ) : null}
              <button
                type="button"
                className={own.ghost}
                data-testid="scope3-decide-conflict-reload"
                onClick={onReload}
              >
                Reload and look
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          {/* The year this decision is for has closed (ADR-0094). An Admin may record it with a
              reason; an exclusion's own justification already counts, so the box is for the
              answers that carry none. Anyone else is told whose act it is. */}
          <PostCloseRetry
            error={refusal}
            pending={decide.isPending}
            onRecord={(postCloseReason) => submit(postCloseReason)}
          />
          <WriteFailed
            error={isPeriodClosed(refusal) ? null : refusal}
            of="this decision"
            onReload={onReload}
          />
        </>
      )}
    </form>
  );
}
